import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WHOLESALE_CONTAINER_LABEL, type WholesaleOrderSummary, type WholesaleAttachment } from '@tucupet/shared';
import { PageHeader, Card, Btn } from '../components/ui';
import { api } from '../lib/api';

interface AdminWholesaleOrder extends WholesaleOrderSummary {
  attachments: Array<WholesaleAttachment & { downloadUrl: string }>;
  bankDetails: null | {
    beneficiary: string; bank: string; iban: string; bic: string; country: string;
    reference: string; amount: number; currency: string;
  };
}

const KIND_LABEL: Record<string, string> = {
  id: 'Carné del representante',
  license: 'Licencia MIPYME / TCP',
  registration: 'Inscripción / Constitución',
  fiscal_address: 'Domicilio fiscal',
  other: 'Otro',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador', submitted: 'En revisión', approved: 'Aprobada', rejected: 'Rechazada',
  paid: 'Pagada', delivered: 'Entregada', cancelled: 'Cancelada',
};
const STATUS_TONE: Record<string, string> = {
  draft: 'var(--text-3)', submitted: 'oklch(0.78 0.16 70)', approved: 'var(--accent)',
  rejected: 'var(--danger)', paid: 'var(--accent)', delivered: 'var(--accent)', cancelled: 'var(--danger)',
};

export default function WholesaleDetailPage() {
  const { code } = useParams();
  const qc = useQueryClient();
  const [reviewNote, setReviewNote] = useState('');
  const [showReject, setShowReject] = useState(false);

  const { data: order, isLoading } = useQuery<AdminWholesaleOrder>({
    queryKey: ['admin', 'wholesale', 'detail', code],
    queryFn: () => api(`/api/admin/wholesale/orders/${code}`),
    enabled: !!code,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'wholesale', 'detail', code] });

  const approve = useMutation({
    mutationFn: () => api(`/api/admin/wholesale/orders/${code}/approve`, { body: { note: reviewNote || undefined } }),
    onSuccess: () => { setReviewNote(''); invalidate(); },
  });
  const reject = useMutation({
    mutationFn: () => api(`/api/admin/wholesale/orders/${code}/reject`, { body: { note: reviewNote } }),
    onSuccess: () => { setReviewNote(''); setShowReject(false); invalidate(); },
  });
  const markPaid = useMutation({
    mutationFn: () => api(`/api/admin/wholesale/orders/${code}/mark-paid`, { body: {} }),
    onSuccess: invalidate,
  });
  const markDelivered = useMutation({
    mutationFn: () => api(`/api/admin/wholesale/orders/${code}/mark-delivered`, { body: {} }),
    onSuccess: invalidate,
  });

  if (isLoading || !order) return <div>Cargando…</div>;

  return (
    <div>
      <Link to="/wholesale" style={{ color: 'var(--text-2)', fontSize: 13, textDecoration: 'none' }}>← Pedidos mayoristas</Link>

      <PageHeader
        kicker="MAYORISTA"
        title={order.code}
        sub={`${order.totalLiters.toLocaleString('es-MX')} L · $${order.totalAmount.toFixed(2)} · creado ${new Date(order.createdAt).toLocaleString('es-MX')}`}
        action={
          <span
            className="mono"
            style={{
              fontSize: 11,
              padding: '6px 12px',
              borderRadius: 999,
              background: `color-mix(in oklab, ${STATUS_TONE[order.status]} 14%, transparent)`,
              color: STATUS_TONE[order.status],
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            {STATUS_LABEL[order.status]}
          </span>
        }
      />

      {/* Action bar */}
      {(order.status === 'submitted' || order.status === 'approved' || order.status === 'paid') && (
        <Card style={{ marginBottom: 20 }}>
          <div className="mono" style={kickerStyle}>ACCIONES</div>
          {order.status === 'submitted' && (
            <>
              <div style={{ marginTop: 12 }}>
                <label className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em' }}>NOTA DE REVISIÓN (OPCIONAL EN APROBACIÓN · REQUERIDA EN RECHAZO)</label>
                <textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  rows={2}
                  placeholder="Comentario interno o motivo de rechazo"
                  style={textareaStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Btn onClick={() => approve.mutate()} disabled={approve.isPending}>
                  {approve.isPending ? '…' : 'Aprobar'}
                </Btn>
                <Btn variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending || reviewNote.trim().length < 5}>
                  {reject.isPending ? '…' : 'Rechazar'}
                </Btn>
              </div>
            </>
          )}
          {order.status === 'approved' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Btn onClick={() => markPaid.mutate()} disabled={markPaid.isPending}>
                {markPaid.isPending ? '…' : 'Marcar pagada'}
              </Btn>
              <div style={{ color: 'var(--text-3)', fontSize: 12, alignSelf: 'center' }}>
                Cuando se concilie la transferencia o se confirme cobro fuera del sistema.
              </div>
            </div>
          )}
          {order.status === 'paid' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Btn onClick={() => markDelivered.mutate()} disabled={markDelivered.isPending}>
                {markDelivered.isPending ? '…' : 'Marcar entregada'}
              </Btn>
              <div style={{ color: 'var(--text-3)', fontSize: 12, alignSelf: 'center' }}>
                Tras coordinar la entrega física de los contenedores.
              </div>
            </div>
          )}
        </Card>
      )}

      {order.reviewerNote && (
        <Card style={{ marginBottom: 20, borderColor: order.status === 'rejected' ? 'var(--danger)' : 'var(--border)' }}>
          <div className="mono" style={{ ...kickerStyle, color: order.status === 'rejected' ? 'var(--danger)' : 'var(--accent)' }}>
            {order.status === 'rejected' ? 'MOTIVO DE RECHAZO' : 'NOTA DEL REVISOR'}
          </div>
          <div style={{ marginTop: 8, fontSize: 14 }}>{order.reviewerNote}</div>
        </Card>
      )}

      {/* Items */}
      <Card style={{ marginBottom: 20 }}>
        <div className="mono" style={kickerStyle}>LÍNEAS</div>
        <div style={{ marginTop: 12 }}>
          {order.items.map((it, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{it.quantity}× {WHOLESALE_CONTAINER_LABEL[it.containerType]}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {it.fuelVariantName} {it.fuelOctane} · ${it.pricePerL.toFixed(3)}/L · {(it.litersPerContainer * it.quantity).toLocaleString('es-MX')} L
                </div>
              </div>
              <div className="mono" style={{ fontWeight: 600 }}>${it.lineTotal.toFixed(2)}</div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0', borderTop: '2px solid var(--border-2)', marginTop: 6 }}>
            <div className="mono" style={{ fontWeight: 600 }}>TOTAL ({order.totalLiters.toLocaleString('es-MX')} L)</div>
            <div className="mono display" style={{ fontSize: 22, fontWeight: 600 }}>${order.totalAmount.toFixed(2)}</div>
          </div>
        </div>
      </Card>

      {/* Customer */}
      {order.customer && (
        <Card style={{ marginBottom: 20 }}>
          <div className="mono" style={kickerStyle}>TITULAR · {order.customer.type.toUpperCase()}</div>
          <KV pairs={[
            ['Razón social', order.customer.businessName],
            ['Identificación', order.customer.taxId],
            ['Representante', `${order.customer.legalRepName} · CI ${order.customer.legalRepId}`],
            ['Domicilio fiscal', `${order.customer.fiscalAddress}, ${order.customer.municipality}, ${order.customer.province}`],
            ['Contacto', `${order.customer.phone} · ${order.customer.email}`],
            ['Actividad', order.customer.activity],
          ]} />
        </Card>
      )}

      {/* Foreign payer */}
      {order.foreignPayer && (
        <Card style={{ marginBottom: 20 }}>
          <div className="mono" style={{ ...kickerStyle, color: 'oklch(0.78 0.16 70)' }}>PAGADOR EXTRANJERO</div>
          <KV pairs={[
            ['Empresa', `${order.foreignPayer.businessName} · ${order.foreignPayer.country}`],
            ['Identificación fiscal', order.foreignPayer.taxId ?? '—'],
            ['Dirección', order.foreignPayer.address ?? '—'],
            ['Contacto', `${order.foreignPayer.contactName} · ${order.foreignPayer.contactEmail} · ${order.foreignPayer.contactPhone}`],
            ['Banco', `${order.foreignPayer.bankName} (${order.foreignPayer.bankCountry})`],
            ['SWIFT / BIC', order.foreignPayer.swiftBic ?? '—'],
          ]} />
        </Card>
      )}

      {/* Notes */}
      {order.notes && (
        <Card style={{ marginBottom: 20 }}>
          <div className="mono" style={kickerStyle}>NOTAS DEL CLIENTE</div>
          <div style={{ marginTop: 8, fontSize: 14, color: 'var(--text-2)' }}>{order.notes}</div>
        </Card>
      )}

      {/* Attachments */}
      <Card style={{ marginBottom: 20 }}>
        <div className="mono" style={kickerStyle}>DOCUMENTOS ADJUNTOS · {order.attachments.length}</div>
        {order.attachments.length === 0 ? (
          <div style={{ marginTop: 10, color: 'var(--text-3)', fontSize: 13 }}>Sin documentos.</div>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {order.attachments.map((a) => (
              <div
                key={a.key}
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {KIND_LABEL[a.kind] ?? a.kind} · {(a.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <a
                  href={a.downloadUrl}
                  target="_blank"
                  rel="noopener"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-2)',
                    color: 'var(--text)',
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  Ver
                </a>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Bank details (if order paid by transfer) */}
      {order.bankDetails && (
        <Card>
          <div className="mono" style={kickerStyle}>PAGO POR TRANSFERENCIA</div>
          <KV pairs={[
            ['Concepto / Ref.', order.bankDetails.reference],
            ['Importe', `$${order.bankDetails.amount.toFixed(2)} ${order.bankDetails.currency}`],
            ['Beneficiario', order.bankDetails.beneficiary],
            ['IBAN', order.bankDetails.iban],
            ...(order.bankDetails.bic ? [['SWIFT', order.bankDetails.bic]] as Array<[string, string]> : []),
          ]} />
        </Card>
      )}
    </div>
  );
}

function KV({ pairs }: { pairs: Array<[string, string]> }) {
  return (
    <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 18px', fontSize: 13 }}>
      {pairs.map(([k, v]) => (
        <React.Fragment key={k}>
          <div style={{ color: 'var(--text-3)' }}>{k}</div>
          <div>{v}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

const kickerStyle: React.CSSProperties = {
  fontSize: 10,
  color: 'var(--text-3)',
  letterSpacing: '0.14em',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 8,
  background: 'var(--surface-2)',
  border: '1px solid var(--border-2)',
  color: 'var(--text)',
  padding: '10px 12px',
  borderRadius: 10,
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};
